import { useState, useEffect, useCallback, useRef } from 'react'
import { Home, Building, Crown, Sparkles, Search, Loader2, SlidersHorizontal } from 'lucide-react'
import { useRooms } from '../hooks/useRooms'
import { RoomData, RoomFilters, RoomSearchParams, getImageUrl, IMAGE_CONFIG } from '../config/settings'
import { resolveSearchCoordinates } from '../lib/googleMaps'

// Import modular components
import RoomsHeader from './rooms/RoomsHeader'
import CategoryNav from './rooms/CategoryNav'
import ResultsHeader from './rooms/ResultsHeader'
import RoomCard from './rooms/RoomCard'
import FilterSidebar from './rooms/FilterSidebar'
import MobileFilterOverlay from './rooms/MobileFilterOverlay'
import CalendarModal from './rooms/CalendarModal'
import { formatApiError, isRetryableApiError } from '../utils/formatApiError'

const Rooms = () => {
  const [filters, setFilters] = useState({
    checkIn: '',
    checkOut: '',
    location: '',
    lat: '',
    lng: '',
    radiusKm: '15',
    beds: 'all',
    baths: 'all',
    guests: 'all',
    sortBy: 'latest',
    priceRange: [1000, 5000],
    amenities: {
      wifi: false,
      parking: false,
      familyRooms: false,
      nonSmoking: false,
      gym: false,
      petsAllowed: false,
      teaCoffee: false,
      roomCleaning: false
    }
  })

  const [selectedCategory, setSelectedCategory] = useState('all')
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [showCalendarModal, setShowCalendarModal] = useState(false)
  const [currentFilters, setCurrentFilters] = useState<RoomFilters>({})
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list')
  const [allRooms, setAllRooms] = useState<RoomData[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [expandedFilterSections, setExpandedFilterSections] = useState<{[key: string]: boolean}>({
    dates: true,
    location: false,
    rooms: false,
    price: false,
    amenities: false
  })
  
  // Use the API hook
  const { 
    rooms, 
    loading, 
    error, 
    pagination, 
    fetchRooms, 
    searchRooms, 
    refetch 
  } = useRooms()

  const categories = [
    { id: 'all', name: 'All', icon: <Home className="h-4 w-4" /> },
    { id: 'regular', name: 'Regular', icon: <Building className="h-4 w-4" /> },
    { id: 'deluxe', name: 'Deluxe', icon: <Crown className="h-4 w-4" /> },
    { id: 'suite', name: 'Suite', icon: <Sparkles className="h-4 w-4" /> },
    { id: 'homestay1', name: '1 RK', icon: <Home className="h-4 w-4" /> },
    { id: 'homestay2', name: '2 BHK', icon: <Home className="h-4 w-4" /> },
    { id: 'homestay3', name: '3 BHK', icon: <Home className="h-4 w-4" /> }
  ]

  const buildLocationApiFields = useCallback(
    (location: string, lat: string, lng: string, radiusKm: string): Pick<RoomFilters, 'location' | 'lat' | 'lng' | 'radius_km'> => {
      const parsedLat = lat.trim() ? Number(lat) : NaN
      const parsedLng = lng.trim() ? Number(lng) : NaN
      if (!Number.isNaN(parsedLat) && !Number.isNaN(parsedLng)) {
        return {
          location: location || undefined,
          lat: parsedLat,
          lng: parsedLng,
          radius_km: radiusKm.trim() || '15',
        }
      }
      return { location: location || undefined }
    },
    [],
  )

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const initialFilters = {
      checkIn: urlParams.get('checkin') || '',
      checkOut: urlParams.get('checkout') || '',
      guests: urlParams.get('guests') || 'all',
      location: urlParams.get('location') || '',
      lat: urlParams.get('lat') || '',
      lng: urlParams.get('lng') || '',
      radiusKm: urlParams.get('radius_km') || '15',
    }
    
    setFilters(prev => ({
      ...prev,
      ...initialFilters
    }))

    const apiFilters: RoomFilters = {
      checkin: initialFilters.checkIn,
      checkout: initialFilters.checkOut,
      guests: initialFilters.guests !== 'all' ? initialFilters.guests : undefined,
      ...buildLocationApiFields(
        initialFilters.location,
        initialFilters.lat,
        initialFilters.lng,
        initialFilters.radiusKm,
      ),
    }

    setCurrentFilters(apiFilters)
    setAllRooms([])
    setHasMore(true)
    fetchRooms(apiFilters)
  }, [])

  useEffect(() => {
    if (rooms.length > 0 && pagination) {
      const currentPage = Number(pagination.current_page) || 0
      const lastPage = Number(pagination.last_page) || 0

      if (currentPage === 1) {
        setAllRooms(rooms)
      } else {
        setAllRooms((prev) => {
          const existingIds = new Set(prev.map((r) => r.id))
          const newRooms = rooms.filter((r) => !existingIds.has(r.id))
          return newRooms.length > 0 ? [...prev, ...newRooms] : prev
        })
        setIsLoadingMore(false)
      }

      setHasMore(currentPage < lastPage)
    } else if (!pagination) {
      setAllRooms([])
      setHasMore(false)
    } else if (pagination.current_page === 1 && rooms.length === 0) {
      setAllRooms([])
      setHasMore(false)
    }
  }, [rooms, pagination])

  const loadMoreRooms = useCallback(async () => {
    if (!pagination || isLoadingMore || loading) {
      return
    }

    const currentPage = Number(pagination.current_page) || 0
    const lastPage = Number(pagination.last_page) || 0
    const nextPage = currentPage + 1
    
    if (nextPage > lastPage) {
      setHasMore(false)
      return
    }

    setIsLoadingMore(true)
    const newFilters = { ...currentFilters, page: nextPage }

    try {
      await fetchRooms(newFilters, true)
    } catch {
      setIsLoadingMore(false)
    }
  }, [pagination, isLoadingMore, loading, currentFilters, fetchRooms])

  // Infinite scroll handler using Intersection Observer
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!sentinelRef.current) return
    if (!pagination || loading || isLoadingMore) return

    const currentPage = Number(pagination.current_page) || 0
    const lastPage = Number(pagination.last_page) || 0
    
    if (currentPage >= lastPage) return // No more pages

    // Calculate threshold based on viewport height (50% of viewport)
    const viewportHeight = window.innerHeight
    const thresholdPx = Math.floor(viewportHeight * 0.5) // 50% of viewport height

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && !isLoadingMore && !loading && pagination) {
          const currentPageNum = Number(pagination.current_page) || 0
          const lastPageNum = Number(pagination.last_page) || 0
          if (currentPageNum < lastPageNum) {
            loadMoreRooms()
          }
        }
      },
      {
        root: null,
        rootMargin: `${thresholdPx}px`, // Trigger at 50% of viewport height (accounts for footer)
        threshold: 0.01
      }
    )

    observer.observe(sentinelRef.current)

    return () => {
      observer.disconnect()
    }
  }, [pagination, loading, isLoadingMore, loadMoreRooms, allRooms.length])

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => {
      const next = { ...prev, [key]: value }
      if (key === 'location') {
        next.lat = ''
        next.lng = ''
      }
      return next
    })
  }

  const handleAmenityChange = (amenity: string) => {
    setFilters(prev => ({
      ...prev,
      amenities: {
        ...prev.amenities,
        [amenity]: !prev.amenities[amenity as keyof typeof prev.amenities]
      }
    }))
  }

  const toggleFilterSection = (section: string) => {
    setExpandedFilterSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const handleApplyFilters = async () => {
    // Convert filter values to proper types
    const parseNumericFilter = (value: string): string | undefined => {
      if (value === 'all' || !value) return undefined
      // Handle "4+" format - remove the + for backend
      if (value.includes('+')) {
        return value.replace('+', '')
      }
      return value
    }

    // Map frontend amenity keys to backend names
    const amenityMap: {[key: string]: string} = {
      wifi: 'Free Wifi',
      parking: 'Parking',
      familyRooms: 'Family Rooms',
      nonSmoking: 'Non Smoking Rooms',
      gym: 'Gym / Fitness Centre',
      petsAllowed: 'Pets Allowed',
      teaCoffee: 'Tea/Coffee Maker in All Rooms',
      roomCleaning: 'Room Cleaning'
    }

    let lat = filters.lat
    let lng = filters.lng
    if (filters.location && (!lat || !lng)) {
      const coords = await resolveSearchCoordinates({ address: filters.location })
      if (coords) {
        lat = String(coords.latitude)
        lng = String(coords.longitude)
        setFilters((prev) => ({ ...prev, lat, lng, location: coords.formattedAddress }))
      }
    }

    const locationFields = buildLocationApiFields(filters.location, lat, lng, filters.radiusKm)

    const searchParams: RoomSearchParams = {
      checkin: filters.checkIn || undefined,
      checkout: filters.checkOut || undefined,
      ...locationFields,
      beds: parseNumericFilter(filters.beds),
      baths: parseNumericFilter(filters.baths),
      guests: parseNumericFilter(filters.guests),
      sort_by: filters.sortBy !== 'latest' ? filters.sortBy : undefined,
      price_min: filters.priceRange[0],
      price_max: filters.priceRange[1],
      ammenities: Object.entries(filters.amenities)
        .filter(([_, value]) => value)
        .map(([key, _]) => amenityMap[key] || key)
    }

    // Convert searchParams to RoomFilters format for currentFilters
    const apiFilters: RoomFilters = {
      checkin: searchParams.checkin,
      checkout: searchParams.checkout,
      ...locationFields,
      beds: searchParams.beds,
      baths: searchParams.baths,
      guests: searchParams.guests,
      sort_by: searchParams.sort_by,
      price_min: searchParams.price_min,
      price_max: searchParams.price_max,
      ammenities: searchParams.ammenities,
    }

    // Reset pagination when filters change
    setAllRooms([])
    setHasMore(true)
    const resetFilters = { ...apiFilters, page: 1 }
    setCurrentFilters(resetFilters)
    await fetchRooms(resetFilters)
    setShowMobileFilters(false)
  }

  const handleCategoryChange = async (categoryId: string) => {
    setSelectedCategory(categoryId)
    
    const locationFields = buildLocationApiFields(
      filters.location,
      filters.lat,
      filters.lng,
      filters.radiusKm,
    )

    const apiFilters: RoomFilters = {
      category: categoryId !== 'all' ? categoryId : undefined,
      checkin: filters.checkIn || undefined,
      checkout: filters.checkOut || undefined,
      guests: filters.guests !== 'all' ? filters.guests : undefined,
      ...locationFields,
    }

    // Reset pagination when category changes
    setAllRooms([])
    setHasMore(true)
    const resetFilters = { ...apiFilters, page: 1 }
    setCurrentFilters(resetFilters)
    await fetchRooms(resetFilters)
  }

  const handleSortChange = (sortBy: string) => {
    handleFilterChange('sortBy', sortBy)
    // Apply filters immediately when sort changes
    setTimeout(() => {
      handleApplyFilters()
    }, 0)
  }

  const handleResetFilters = () => {
    setFilters({
      checkIn: '',
      checkOut: '',
      location: '',
      lat: '',
      lng: '',
      radiusKm: '15',
      beds: 'all',
      baths: 'all',
      guests: 'all',
      sortBy: 'latest',
      priceRange: [1000, 5000],
      amenities: {
        wifi: false,
        parking: false,
        familyRooms: false,
        nonSmoking: false,
        gym: false,
        petsAllowed: false,
        teaCoffee: false,
        roomCleaning: false
      }
    })
  }

  const handlePageChange = (page: number) => {
    // Ensure page is a valid positive integer
    const validPage = Number.isInteger(page) && page > 0 ? page : 1
    const newFilters = { ...currentFilters, page: validPage }
    setCurrentFilters(newFilters)
    fetchRooms(newFilters)
  }

  // Transform API room data to match component expectations
  const transformedRooms = allRooms.map((room: any) => {
    // Handle image URL - use 3rd image from slider_imgs if available, otherwise featured_img
    let imageUrl = IMAGE_CONFIG.FALLBACK_IMAGE
    
    // Try to get 3rd image from slider_imgs
    if (room.slider_imgs) {
      try {
        const sliderImgs = typeof room.slider_imgs === 'string' 
          ? JSON.parse(room.slider_imgs) 
          : room.slider_imgs
        
        if (Array.isArray(sliderImgs) && sliderImgs.length >= 3) {
          // Use 3rd image (index 2)
          imageUrl = getImageUrl(sliderImgs[2])
        } else if (Array.isArray(sliderImgs) && sliderImgs.length > 0) {
          // Fallback to last image if less than 3
          imageUrl = getImageUrl(sliderImgs[sliderImgs.length - 1])
        }
      } catch (e) {
        console.warn('Failed to parse slider_imgs:', e)
      }
    }
    
    // Fallback to featured_img if no slider images
    if (imageUrl === IMAGE_CONFIG.FALLBACK_IMAGE && room.featured_img) {
      if (room.featured_img.startsWith('http://') || room.featured_img.startsWith('https://')) {
        imageUrl = room.featured_img
      } else if (room.featured_img.startsWith('/')) {
        imageUrl = getImageUrl(room.featured_img)
      } else {
        // Just filename, construct the path
        imageUrl = getImageUrl(`/assets/img/rooms/${room.featured_img}`)
      }
    }
    
    return {
      id: room.room_id || room.id,
      title: room.title,
      roomNumber: room.room_id?.toString() || room.id.toString(),
      provider: room.vendor_username || 'Aonla Stay',
      price: room.rent,
      rating: room.rating || 0,
      beds: room.bed,
      baths: room.bath,
      guests: room.max_guests,
      image: imageUrl,
      amenities: room.amenities || [],
      category: room.category_name?.toLowerCase() || 'regular',
      summary: room.summary
    }
  })

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Calendar Modal */}
      <CalendarModal
        isOpen={showCalendarModal}
        onClose={() => setShowCalendarModal(false)}
        checkIn={filters.checkIn}
        checkOut={filters.checkOut}
        onDateChange={(name, value) => {
          handleFilterChange(name, value)
        }}
      />

      {/* Mobile Filter Overlay */}
      <MobileFilterOverlay
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        filters={filters}
        expandedSections={expandedFilterSections}
        loading={loading}
        onFilterChange={handleFilterChange}
        onAmenityChange={handleAmenityChange}
        onToggleSection={toggleFilterSection}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
      />

      {/* Header */}
      <RoomsHeader onBack={() => window.history.back()} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Category Navigation */}
        <div className="mb-4 sm:mb-6">
          <CategoryNav
            categories={categories}
            selectedCategory={selectedCategory}
            loading={loading}
            onCategoryChange={handleCategoryChange}
          />
        </div>

        {/* Results Header */}
        <ResultsHeader
          loading={loading}
          roomCount={transformedRooms.length}
          checkIn={filters.checkIn}
          checkOut={filters.checkOut}
          guests={filters.guests}
          pagination={pagination ? {
            from: pagination.from,
            to: pagination.to,
            total: pagination.total
          } : undefined}
          viewMode={viewMode}
          sortBy={filters.sortBy}
          onViewModeChange={setViewMode}
          onSortChange={handleSortChange}
          onFilterClick={() => setShowMobileFilters(true)}
        />

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Error State */}
            {error && (
              <div
                className={`rounded-xl p-4 mb-6 border ${
                  isRetryableApiError(error)
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isRetryableApiError(error) ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                  >
                    <span className="text-white text-sm font-bold">!</span>
                  </div>
                  <div className="flex-1">
                    <p
                      className={`font-semibold ${
                        isRetryableApiError(error) ? 'text-amber-900' : 'text-red-800'
                      }`}
                    >
                      {isRetryableApiError(error) ? 'Temporarily unavailable' : 'Could not load listings'}
                    </p>
                    <p
                      className={`text-sm mt-1 ${
                        isRetryableApiError(error) ? 'text-amber-800' : 'text-red-600'
                      }`}
                    >
                      {formatApiError(error)}
                    </p>
                  </div>
                  <button
                    onClick={refetch}
                    className={`px-4 py-2 text-white rounded-lg transition-colors text-sm font-medium ${
                      isRetryableApiError(error)
                        ? 'bg-amber-600 hover:bg-amber-700'
                        : 'bg-red-500 hover:bg-red-600'
                    }`}
                  >
                    Retry
                  </button>
                </div>
              </div>
            )}

            {/* Loading State - Only show on initial load when no rooms are loaded */}
            {loading && allRooms.length === 0 && (
              <div className="flex items-center justify-center py-20">
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="h-10 w-10 animate-spin text-primary-500" />
                  <span className="text-gray-600 font-medium">Loading properties...</span>
                </div>
              </div>
            )}

            {/* Room Cards - Grid View - Always show if we have rooms, even when loading more */}
            {allRooms.length > 0 && viewMode === 'grid' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {transformedRooms.map((room) => (
                  <RoomCard
                    key={room.id}
                    room={room}
                    viewMode="grid"
                    checkIn={filters.checkIn}
                    checkOut={filters.checkOut}
                    guests={filters.guests}
                  />
                ))}
              </div>
            )}

            {/* Room Cards - List View - Always show if we have rooms, even when loading more */}
            {allRooms.length > 0 && viewMode === 'list' && (
              <div className="space-y-4 sm:space-y-6">
                {transformedRooms.map((room) => (
                  <RoomCard
                    key={room.id}
                    room={room}
                    viewMode="list"
                    checkIn={filters.checkIn}
                    checkOut={filters.checkOut}
                    guests={filters.guests}
                  />
                ))}
              </div>
            )}

            {/* No Results - Only show when not loading and no rooms */}
            {!loading && allRooms.length === 0 && !error && (
              <div className="text-center py-16 sm:py-20">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">No properties found</h3>
                <p className="text-gray-600 mb-6">Try adjusting your filters to see more results</p>
                <button 
                  onClick={refetch}
                  className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-semibold shadow-md"
                >
                  Refresh
                </button>
              </div>
            )}

            {/* Loading More Indicator - Show below existing cards when loading more */}
            {isLoadingMore && allRooms.length > 0 && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
                <span className="ml-3 text-gray-600 font-medium">Loading more properties...</span>
              </div>
            )}

            {/* Infinite Scroll Sentinel - Hidden element for auto-scroll pagination */}
            {!isLoadingMore && pagination && allRooms.length > 0 && (() => {
              const currentPage = Number(pagination.current_page) || 0;
              const lastPage = Number(pagination.last_page) || 0;
              const hasMorePages = currentPage < lastPage;
              
              if (hasMorePages) {
                return (
                  <div 
                    ref={sentinelRef}
                    className="h-1 w-full" 
                    style={{ marginTop: '100px', marginBottom: '50px' }} 
                    aria-hidden="true"
                  />
                );
              }
              return null;
            })()}

            {/* Load More Button - Manual trigger option (works alongside auto-scroll) */}
            {!isLoadingMore && pagination && allRooms.length > 0 && (() => {
              const currentPage = Number(pagination.current_page) || 0;
              const lastPage = Number(pagination.last_page) || 0;
              const hasMorePages = currentPage < lastPage;
              
              if (hasMorePages) {
                return (
                  <div className="flex flex-col items-center justify-center py-8">
                    <button
                      onClick={loadMoreRooms}
                      disabled={isLoadingMore || loading}
                      className="px-8 py-3 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md"
                    >
                      <span>Load More Properties</span>
                    </button>
                    <p className="text-sm text-gray-500 mt-3">
                      Showing {allRooms.length} of {pagination.total} properties
                    </p>
                  </div>
                );
              } else {
                return (
                  <div className="text-center py-8 text-gray-500">
                    <p>You've reached the end of the results</p>
                    <p className="text-sm mt-1">Showing all {pagination.total} propert{pagination.total !== 1 ? 'ies' : 'y'}</p>
                  </div>
                );
              }
            })()}
          </div>

          {/* Filter Sidebar - Desktop Only */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto">
              <div className="flex items-center gap-2 mb-6">
                <SlidersHorizontal className="h-5 w-5 text-primary-500" />
                <h3 className="text-xl font-bold text-gray-900">Filters</h3>
              </div>
              <FilterSidebar
                filters={filters}
                expandedSections={expandedFilterSections}
                loading={loading}
                onFilterChange={handleFilterChange}
                onAmenityChange={handleAmenityChange}
                onToggleSection={toggleFilterSection}
                onApplyFilters={handleApplyFilters}
                onResetFilters={handleResetFilters}
                onOpenCalendar={() => setShowCalendarModal(true)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Rooms
